import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import NativeModal from 'react-modal';
import { connect } from 'react-redux';

import { MOBILE_BREAKPOINT } from '@xanadu/ui-shared/constants';

import ChildrenPropsContext from '@xanadu/ui-shared/contexts/ChildrenPropsContext';
import {
  displayModal as displayModalAct
} from '@xanadu/ui-shared/modules/modal/actions';

import IconClose from '../../../../images/icons/misc/close.svg';

import styles from './Modal.module.scss';
import './overrideReactModal.scss';

NativeModal.setAppElement(document.getElementById('root'));

const Modal = ({
  currency,
  userStatus,
  config,
  displayModal,
  contents,
  widthSize,
  onClose = () => { },
}) => {
  const {
    section,
    show,
    showCloseIcon,
    hasDepositPromptMessage,
    email,
    userRegistered,
    extraData,
    position,
    smallModal
  } = config || {};

  const upperContext = React.useContext(ChildrenPropsContext);

  const [overlayMounted, setOverlayMounted] = useState(false);

  const prevShow = useRef(show);

  const userIsLocked = userStatus === 'locked';

  const handlePageScroll = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    return false;
  }, []);

  const closeModal = () => {
    displayModal({
      show: false,
      section: 'default',
    });

    onClose();
  };

  const handleOnCloseModal = () => {
    closeModal();
  };

  const {
    component: Content,
    componentProps = {},
    showCloseIcon: contentShowCloseIcon,
    layout,
    size = {},
  } = useMemo(() => {
    const selectedContent = contents.find(content => content.section === section);

    if (selectedContent) {
      return selectedContent;
    }

    return {};
  }, [section, contents]);

  const useCustomLayout = layout === 'custom';

  useEffect(() => () => {
    window.removeEventListener('scroll', handlePageScroll);
  }, []);

  useEffect(() => {
    if (!prevShow.current && show) {
      window.addEventListener('scroll', handlePageScroll);
    }

    if (prevShow.current && !show) {
      window.removeEventListener('scroll', handlePageScroll);
    }

    prevShow.current = show;

    setTimeout(() => {
      setOverlayMounted(show);
    }, 0);
  }, [show]);

  const modalStyle = {
    ...(useCustomLayout ? {
      ...(size.height ? { height: size.height } : {}),
      ...(size.width ? { width: size.width } : {}),
      ...(size.minWidth ? { minWidth: size.minWidth } : {}),
      ...(size.minHeight ? { minHeight: size.minHeight } : {}),
    } : {})
  };

  const reactModalClass = classNames({
    'react-modal': true,
    'react-modal--center': widthSize > MOBILE_BREAKPOINT || position === 'center',
    'react-modal--bottom': position === 'bottom',
    'react-modal--registration': section === 'registration',
    'react-modal--fullscreen': layout === 'fullscreen',
    'react-modal--large': layout === 'large',
    'react-modal--medium': layout === 'medium' || !layout,
    'react-modal--small': layout === 'small' || smallModal,
    'react-modal--small--floating': layout === 'small-floating',
    'react-modal--auto': layout === 'auto',
  });

  const reactModalOverlayClass = classNames({
    [styles.overlay]: true,
    [styles['overlay--mounted']]: overlayMounted,
    [styles['overlay--obfuscated']]: userIsLocked,
  });

  const modalClass = classNames({
    [styles.main]: true,
    [styles['main--fullscreen']]: layout === 'fullscreen',
    [styles['main--large']]: layout === 'large',
    [styles['main--medium']]: layout === 'medium' || !layout,
    [styles['main--small']]: layout === 'small',
  });

  let shouldShowCloseIcon = true;

  if (typeof showCloseIcon === 'boolean') {
    shouldShowCloseIcon = showCloseIcon;
  }

  if (typeof contentShowCloseIcon === 'boolean') { // this is the value coming from section
    shouldShowCloseIcon = contentShowCloseIcon;
  }

  // Apply high z-index for deposit modal in mobile (to appear above ExchangeBettingModal)
  const portalClassName = section === 'deposit'
    ? 'ReactModalPortal ReactModalPortal--deposit'
    : undefined;

  return (
    <NativeModal
      isOpen={show}
      contentLabel="Modal"
      className={reactModalClass}
      overlayClassName={reactModalOverlayClass}
      shouldCloseOnOverlayClick
      portalClassName={portalClassName}
    >
      <div className={modalClass} style={modalStyle}>
        {shouldShowCloseIcon && (
          <div
            role="button"
            tabIndex={0}
            className={styles.close}
            onClick={handleOnCloseModal}
            data-hook="dialogboxclose"
          >
            <IconClose
              className={styles.icon}
            />
          </div>
        )}
        <div className={styles.content}>
          {Content && (
            <ChildrenPropsContext.Provider
              value={{
                ...upperContext,
                childrenProps: componentProps.childrenProps,
              }}
            >
              <Content
                currency={currency}
                closeModal={closeModal}
                hasDepositPromptMessage={hasDepositPromptMessage}
                email={email}
                userRegistered={userRegistered}
                extraData={extraData}
                onRequestClose={handleOnCloseModal}
                userIsLocked={userIsLocked}
                {...componentProps}
              />
            </ChildrenPropsContext.Provider>
          )}
        </div>
      </div>
    </NativeModal>
  );
};

Modal.propTypes = {
  config: PropTypes.instanceOf(Object).isRequired,
  displayModal: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired,
  userStatus: PropTypes.string.isRequired,
  contents: PropTypes.instanceOf(Array).isRequired,
  onClose: PropTypes.func,
  widthSize: PropTypes.number,
};

const mapDispatchToProps = {
  displayModal: displayModalAct,
};

function mapStateToProps(state) {
  return {
    config: state.get('modal'),
    userStatus: state.getIn(['user', 'status']),
    currency: state.getIn(['user', 'currency']),
    widthSize: state.getIn(['application', 'widthSize']),
  };
}

export const Component = Modal;

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
